import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  DomSanitizer,
  SafeHtml,
  SafeResourceUrl,
} from "@angular/platform-browser";
import { createDialogConfig } from "@utils";
import { Observable, Subject, first } from "rxjs";

/** How long to wait for the form before showing the load error. */
export const G2_REVIEW_FORM_LOAD_TIMEOUT_MS = 20_000;

export interface G2ReviewFormDialogData {
  /**
   * The G2 review form URL built by the Celo backend. It contains the user's
   * email address, so it must never be logged.
   */
  reviewUrl?: string;

  /**
   * Static HTML shown in place of the G2 form. Used only by the development
   * dialog gallery, which has no G2 token to build a real review URL.
   */
  placeholderHtml?: string;
}

type FormState = "loading" | "loaded" | "failed";

@Component({
  selector: "app-g2-review-form-dialog",
  templateUrl: "./g2-review-form-dialog.component.html",
  styleUrls: ["./g2-review-form-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class G2ReviewFormDialogComponent implements OnDestroy {
  public readonly reviewUrl?: SafeResourceUrl;
  public readonly placeholderHtml?: SafeHtml;
  public state: FormState = "loading";
  /** Changing this re-creates the iframe, which reloads the same URL. */
  public loadAttempt = 0;

  private loadedSubject = new Subject<void>();
  private loadTimeout?: ReturnType<typeof setTimeout>;
  private readonly onOffline = () => this.fail();

  /**
   * Emits once, the first time the form finishes loading. A cross-origin
   * iframe also fires `load` for the browser's own error page, so a load
   * failure is detected from being offline or from the timeout instead.
   */
  public readonly formLoaded: Observable<void> =
    this.loadedSubject.pipe(first());

  public constructor(
    @Inject(MAT_DIALOG_DATA) dialogData: G2ReviewFormDialogData,
    sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    // Both values come from Celo code, not from user input.
    if (dialogData.placeholderHtml) {
      this.placeholderHtml = sanitizer.bypassSecurityTrustHtml(
        dialogData.placeholderHtml
      );
    }
    if (dialogData.reviewUrl) {
      this.reviewUrl = sanitizer.bypassSecurityTrustResourceUrl(
        dialogData.reviewUrl
      );
    }
    window.addEventListener("offline", this.onOffline);
    this.startLoading();
  }

  public static openDialog(
    matDialog: MatDialog,
    data: G2ReviewFormDialogData
  ): MatDialogRef<G2ReviewFormDialogComponent> {
    const config = createDialogConfig<G2ReviewFormDialogData>(data, {
      panelClasses: ["g2-review-form-dialog"],
      width: "860px",
      height: "680px",
      maxWidth: "95vw",
      maxHeight: "95vh",
    });
    return matDialog.open<G2ReviewFormDialogComponent, G2ReviewFormDialogData>(
      G2ReviewFormDialogComponent,
      config
    );
  }

  public ngOnDestroy(): void {
    window.removeEventListener("offline", this.onOffline);
    this.clearLoadTimeout();
    this.loadedSubject.complete();
  }

  public onFormLoaded(): void {
    if (this.state !== "loading") return;
    this.clearLoadTimeout();
    this.state = "loaded";
    this.loadedSubject.next();
    this.changeDetectorRef.markForCheck();
  }

  /** Reloads the same URL. The rating is not sent again. */
  public retry(): void {
    this.loadAttempt++;
    this.startLoading();
  }

  private startLoading(): void {
    this.state = "loading";
    this.clearLoadTimeout();
    if (!navigator.onLine) {
      this.fail();
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.loadTimeout = setTimeout(
        () => this.ngZone.run(() => this.fail()),
        G2_REVIEW_FORM_LOAD_TIMEOUT_MS
      );
    });
    this.changeDetectorRef.markForCheck();
  }

  private fail(): void {
    if (this.state !== "loading") return;
    this.clearLoadTimeout();
    this.state = "failed";
    this.changeDetectorRef.markForCheck();
  }

  private clearLoadTimeout(): void {
    if (this.loadTimeout) clearTimeout(this.loadTimeout);
    this.loadTimeout = undefined;
  }
}
